import {AbstractInteractionMode, GeometryCoordinate} from "../interaction-mode/abstract-interaction-mode";
import {MapController} from "../map-controller";
import {DrawCustomMode, DrawCustomModeThis, DrawFeature} from "@mapbox/mapbox-gl-draw";
import {SnapMode, SnapPoint} from "../snap";
import {MenuItem} from "primevue/menuitem";
import {Feature, GeoJSON, Geometry, LineString, MultiPoint, MultiPolygon, Position} from "geojson";
import {DrawFeatureOptions, DrawGLUtil} from "../draw-modes/abstract-draw-mode";
import * as CommonSelectors from '@mapbox/mapbox-gl-draw/src/lib/common_selectors';
import doubleClickZoom from '@mapbox/mapbox-gl-draw/src/lib/double_click_zoom';
import mouseEventPoint from '@mapbox/mapbox-gl-draw/src/lib/mouse_event_point';
import {MapEvent} from "../../document/model/map";
import {LayerGeometry} from "../../document/model/layer";
import {RendererUtils} from "../renderer/renderer-utils";
import {FeatureUtil} from "../feature";
import {GeometryUtil} from "../geometry";
import {areaLabelFormat, perimeterLabelFormat, distanceLabelFormat, getArrayDepth} from "../../../js/utils"
import {position} from "html2canvas/dist/types/css/property-descriptors/position";
import {v4 as uuid} from 'uuid';
import {ShapeGenerator} from "../generators/shape-generator";

export class InteractionState {
  features: Map<string, Feature> = new Map();
  featureID: string | undefined;

  snapFeature: LayerGeometry | undefined;
  snapFeaturePath: string | undefined;

  constructor(
    features: Feature[] = [],
    public isDirty: boolean = false
  ) {
    features.forEach(feature => {
      if (!this.featureID) {
        this.featureID = (feature.id ?? feature.properties?.id ?? uuid()).toString();
      }
      this.features.set(this.featureID, feature);
    });
  }

  getFeature = (): Feature | undefined => {
    return this.features.get(this.featureID);
  }

  setFeature = (feature: Feature): void => {
    if (this.features && feature) this.features.set((feature.id || feature.properties?.id).toString(), feature);
  }

}

export abstract class AbstractInteraction<StateType extends InteractionState> {
  protected commonSelectors: typeof CommonSelectors;
  protected doubleClickZoom: typeof doubleClickZoom;
  protected mouseEventPoint;
  protected snapPoint: SnapPoint | undefined;

  constructor(
    protected mode: AbstractInteractionMode,
    protected controller: MapController,
    public options: Partial<InteractionOptions>
  ) {
    this.commonSelectors = CommonSelectors;
    this.doubleClickZoom = doubleClickZoom;
    this.mouseEventPoint = (mouseEvent) => {
      return mouseEventPoint(mouseEvent, this.controller.mapInstance.getContainer())
    };
  }

  useHistory(state: StateType): boolean {
    return true;
  }

  display(state: StateType, geojson: GeoJSON & Feature, display: (geojson: GeoJSON) => void,): any {
    let layerGeometry = this.controller.interaction.getLayerGeometry(geojson.properties.id);
    let center;
    switch (geojson.geometry.type) {
      case 'Point':
        if (geojson.properties.internal) {
          break;
        }
        //draw selection points as aims for better visibility
        let icon = layerGeometry?.document?.properties?.selection || layerGeometry?.document?.properties?.withoutSelectionIcon ? `selection_${layerGeometry.documentId}` : 'auto-aim';

        geojson = this.createIcon(geojson.geometry.coordinates, icon, {
          id: geojson.properties.id,
          "icon-size": layerGeometry?.document?.properties?.selection ? 0.1 : 1
        });
        break;
      case 'Polygon':
      case 'MultiPolygon':
        if (geojson.properties._showVertexLength || /*layerGeometry?.geometry?.properties?._showVertexLength ||*/ this.controller.interaction.hasVertexLength()) {
          switch (geojson.geometry.type) {
            case 'Polygon':
              geojson.geometry.coordinates.forEach(ring => this._generateVertexLengths(ring, display, geojson));
              break;
            case 'MultiPolygon':
              geojson.geometry.coordinates.forEach(subFeature => {
                subFeature.forEach(ring => this._generateVertexLengths(ring, display, geojson));
              });
          }
        }

        if ((geojson.properties._showArea || /*layerGeometry?.geometry?.properties?._showArea ||*/ this.controller.interaction.hasArea()) && (geojson.properties._showLength || /*layerGeometry?.geometry?.properties?._showLength ||*/ this.controller.interaction.hasLength())) {
          let area = GeometryUtil.area(geojson);
          let length = GeometryUtil.calculatePerimeter(geojson?.geometry?.coordinates);
          center = GeometryUtil.centerOfMass(geojson);
          display(this.createLabel(center, `A: ${areaLabelFormat(area)}\nP: ${perimeterLabelFormat(length)}`, {internal: false, parent: geojson.properties.id}));
        }
        else {
          if (geojson.properties._showArea || /*layerGeometry?.geometry?.properties?._showArea ||*/ this.controller.interaction.hasArea()) {
            let area = GeometryUtil.area(geojson);
            center = GeometryUtil.centerOfMass(geojson);
            display(this.createLabel(center, `A: ${areaLabelFormat(area)}`, {internal: false, parent: geojson.properties.id}));
          }
          if (geojson.properties._showLength || /*layerGeometry?.geometry?.properties?._showLength ||*/ this.controller.interaction.hasLength()) {
            switch (geojson.geometry.type) {
              case 'Polygon':
              case 'MultiPolygon':
                let length = GeometryUtil.calculatePerimeter(geojson?.geometry?.coordinates);
                center = GeometryUtil.centerOfMass(geojson);
                display(this.createLabel(center, `P: ${perimeterLabelFormat(length)}`, {internal: false, parent: geojson.properties.id}));
            }
          }
        }

        if (geojson.properties._showAngle || this.controller.interaction.hasAngle()) {
          (getArrayDepth(geojson.geometry.coordinates) === 3 ? [geojson.geometry.coordinates] : geojson.geometry.coordinates).forEach(polygon => {

            if (polygon[0]?.length > 3)
              for (var i = 2; i < polygon[0].length + 1; i++) {
                let measurement = ShapeGenerator.generateAngleMeasurement(
                  polygon[0][i >= polygon[0].length ? polygon[0].length - 2 : i - 2],
                  polygon[0][i >= polygon[0].length ? 0 : i - 1],
                  polygon[0][i >= polygon[0].length ? 1 : i]
                );
                display(measurement);
                let label = {
                  type: "Feature",
                  properties: {
                    meta: "label",
                    parent: geojson.properties.id,
                    label: measurement.properties.label.toLocaleString() + "°"
                  },
                  geometry: {type: "Point", coordinates: measurement.properties.labelPosition}
                };
                display(label);
              }

          })

        }

        break;
      case 'LineString':
      case 'MultiLineString':
        if (geojson.properties._showLength || /*layerGeometry?.geometry?.properties?._showLength ||*/ this.controller.interaction.hasLength()) {
          let length = GeometryUtil.totalLength(geojson);
          center = GeometryUtil.alongLine(geojson as Feature<LineString>, length / 2);

          display(this.createLabel(center, distanceLabelFormat(length), {
            internal: false,
            parent: geojson.properties.id
          }));
        }

        if (geojson.properties._showVertexLength || this.controller.interaction.hasVertexLength()) {
          switch (geojson.geometry.type) {
            case 'LineString':
              this._generateVertexLengths(geojson.geometry.coordinates, display, geojson);
              break;
            case 'MultiLineString':
              geojson.geometry.coordinates.forEach(subFeature => this._generateVertexLengths(subFeature, display, geojson));
              break;
          }
        }
        if (geojson.properties._showAngle || this.controller.interaction.hasAngle()) {
            (getArrayDepth(geojson.geometry.coordinates) === 2 ? [geojson.geometry.coordinates] : geojson.geometry.coordinates).forEach(line => {
              if (line?.length > 2)
                for (var i = 2; i < line.length; i++) {
                  let measurement = ShapeGenerator.generateAngleMeasurement(
                    line[i - 2],
                    line[i - 1],
                    line[i]
                  );
                  display(measurement);
                  let label = {
                    type: "Feature",
                    properties: {
                      meta: "label",
                      parent: geojson.properties.id,
                      label: measurement.properties.label.toLocaleString() + "°"
                    },
                    geometry: {type: "Point", coordinates: measurement.properties.labelPosition}
                  };
                  display(label);
                }

            })
        }

        break;
    }

    if (this.controller.interaction.mode.getIdentifier() === 'drawer') {
      let tmpGeometry: any = this.controller.interaction.getTmpGeometry(geojson.properties.id)
      if (tmpGeometry) {
        geojson.properties = {...(geojson?.properties || {}), ...(tmpGeometry?.properties || {})}
      }
      else {
        let properties = this.controller.interaction.getConfigurationDrawStyle();
        geojson.properties = {...(geojson?.properties || {}), ...(properties || {})}
      }
    }

    display(geojson);
  }

  onStop(state: StateType) {
    this.clearDrawFeatures();
  }

  async onClick(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onMouseMove(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onMouseDown(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onMouseUp(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onMouseOut(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onKeyUp(state: StateType, e: KeyboardEvent) {
    switch (true) {
      case this.commonSelectors.isEscapeKey(e) && this.controller.interaction.hasHistory():
        this.controller.interaction.historyUndo();
        break;
      case this.commonSelectors.isEnterKey(e) && this.controller.interaction.mode.canSave():
        this.controller.interaction.historySave();
        break;
      case e.key === 'Delete':
        this.controller.interaction.attemptDeleteFocus(e);
        break;
      case e.key === 'Tab':
        if (!this.isManualInputSupported(state)) {
          break;
        }
        if (this.controller.interaction.hasManualInputDialog()) {
          this.controller.interaction.hideManualInputDialog();
        } else {
          this.controller.interaction.showManualInputDialog();
        }
        break;
    }
  }

  async onKeyDown(state: StateType, e: KeyboardEvent) {
  }

  async onDrag(state: StateType, e: mapboxgl.MapMouseEvent) {
  }

  async onTouchStart(state: StateType, e: mapboxgl.MapTouchEvent) {
  }

  async onTouchEnd(state: StateType, e: mapboxgl.MapTouchEvent) {
  }

  async onTouchMove(state: StateType, e: mapboxgl.MapTouchEvent) {
  }

  async onTap(state: StateType, e: mapboxgl.MapTouchEvent) {
  }

  onSnapModeChange(state: StateType, mode: SnapMode): void {
  }

  buildInitialState(initialState: StateType): StateType {
    // @ts-ignore
    return new InteractionState(initialState.features ? Array.from(initialState.features.values()) : [], initialState.isDirty);
  }

  buildContextMenu(state: StateType): MenuItem[] {
    return [];
  }

  getSupportedSnapModes(): SnapMode[] {
    return [];
  }

  canSave(state: StateType): boolean {
    return true;
  }

  showHistory(state: StateType): boolean {
    return true;
  }

  isSnapModeSupported(mode: SnapMode): boolean {
    return this.getSupportedSnapModes().indexOf(mode) >= 0;
  }

  isTraceModeAccepted(state: StateType): boolean {
    return false;
  }

  isManualInputSupported(state: StateType): boolean {
    return false;
  }

  async setCoordinates(state: StateType, points: Position[]): Promise<void> {
    this.controller.interaction.historyPush(state);
    let feature = state.features.get(state.featureID);
    switch (feature.geometry.type) {
      case 'Point':
        feature.geometry.coordinates = points[0];
        break;
      case 'LineString':
        feature.geometry.coordinates = [...points];
        break;
      case 'MultiLineString':
        feature.geometry.coordinates = [[...points]];
        break;
      case 'Polygon':
        feature.geometry.coordinates = [[...points, points[0]]];
        break;
      case 'MultiPolygon':
        feature.geometry.coordinates = [[[...points, points[0]]]];
        break;
    }
    this.controller.interaction.mode.add(feature);
    this.tick();
  }

  getCoordinates(state: StateType): GeometryCoordinate[] {
    let coordinates = [];
    if (!state.features?.size || state.features.size === 0) {
      return [];
    }
    FeatureUtil.decompose(state.features.get(state.featureID)).vertexMap.forEach((coordinate, path) => {
      coordinates.push({path, coordinate});
    });
    return coordinates;
  }

  save(state: StateType, nextInteraction: string | undefined = undefined): void {
    this.controller.interaction.historyClear();
    if (nextInteraction) {
      this.mode.changeInteraction(nextInteraction);
    }
  }

  protected displayDrawFeature(feature: DrawFeature | Feature): void {
    this.controller.interaction.mode.currentInteractionAPI.store.add(feature);
    this.controller.interaction.mode.currentInteractionAPI.store.render();
  }

  public removeDrawFeature(featureId: string | number): void {
    this.controller.interaction.mode.currentInteractionAPI.store.delete(featureId.toString());
    this.controller.interaction.mode.currentInteractionAPI.store.render();
  }

  public clearDrawFeatures(): void {
    this.controller.interaction.mode.currentInteractionAPI.store.delete(this.controller.interaction.mode.currentInteractionAPI.store.getAllIds());
  }

  protected createAIM(parent: string | undefined = undefined, originalLocation: Position | undefined = undefined, id: string | undefined = undefined, icon: string = undefined) {
    let opts: Partial<DrawFeatureOptions> = {id: id ? id : 'aim'};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createIcon(originalLocation ?? this.controller.mapInstance.getCenter(), icon ? icon : this.getAIMIcon(), opts);
  }

  protected getAIMIcon(): string {
    return this.controller.configuration.cursor ?? 'auto-aim';
  }

  protected createFeature(feature: Feature): DrawFeature {
    return this.mode.currentInteractionProxy.newFeature(JSON.parse(JSON.stringify(feature)));
  }

  protected createFeatureFromLayerGeometry(layerGeometry: LayerGeometry): DrawFeature {
    return this.mode.currentInteractionProxy.newFeature({id: layerGeometry.geometryId.toString(), ...layerGeometry.geometry})
  }

  protected createIcon(position: Position, icon: string, opts: Partial<DrawFeatureOptions & DrawIconOptions> = {}) {
    return DrawGLUtil.createIcon(position, icon, opts);
  }

  protected createLabel(position: Position, label: string, opts: Partial<DrawFeatureOptions> = {}) {
    return DrawGLUtil.createLabel(position, label, opts);
  }

  protected createPoint(position: Position, id: string, parent: string | undefined = undefined) {
    let opts: Partial<DrawFeatureOptions> = {id};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createPoint(position, opts);
  }

  protected createMultiPoint(points: Position[], id: string, parent: string | undefined = undefined): Feature<MultiPoint> {
    let opts: Partial<DrawFeatureOptions> = {id};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createMultiPoint(points, opts);
  }

  protected createLineString(points: Position[], id: string, parent: string | undefined = undefined) {
    let opts: Partial<DrawFeatureOptions> = {id};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createLineString(points, opts);
  }

  protected createPolygon(points: Position[], id: string, parent: string | undefined = undefined) {
    let opts: Partial<DrawFeatureOptions> = {id};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createPolygon(points, opts);
  }

  protected createMultiPolygon(points: Position[][], id: string, parent: string | undefined = undefined): Feature<MultiPolygon> {
    let opts: Partial<DrawFeatureOptions> = {id};
    if (parent) {
      opts.parent = parent;
    }
    return DrawGLUtil.createMultiPolygon(points, opts);
  }

  protected createLayerGeometries(features: Feature[], nextInteraction: string | undefined = undefined): void {
    this.controller.mapInstance.fire(MapEvent.LayerGeometryCreate, {features, nextInteraction});
  }

  protected tick() {
    this.controller.mapInstance.fire('tick');
  }

  protected _generateVertexLengths(ring: Position[], display, geojson) {
    let lastPosition = undefined;
    ring.forEach(position => {
      if (lastPosition) {
        let distance = GeometryUtil.pointsDistance(lastPosition, position);
        let center = GeometryUtil.midpoint(lastPosition, position);
        display(this.createLabel(center, distanceLabelFormat(distance), {
          internal: false,
          parent: geojson.properties.id
        }));
      }
      lastPosition = position;
    })
  }

  static definition(options: Partial<InteractionOptions> = {}): InteractionDefinition {
    return new InteractionDefinition(this, options)
  }
}

export class InteractionDefinition {
  constructor(
    public interaction: typeof AbstractInteraction,
    public options: Partial<InteractionOptions> = {}
  ) {
  }
}

export interface InteractionOptions {
  selectionRequired: boolean;
  selectionAllowed: boolean;
  multiSelection: boolean;
  showVertexLength: boolean;
  showTotalLength: boolean;
  priorityContextActions: boolean;
  changeset: string;
}

export function InteractionProxy(
  identifier: string,
  definition: InteractionDefinition,
  parentMode: AbstractInteractionMode,
  controller: MapController
): {proxy: DrawCustomModeThis & DrawCustomMode, instance: AbstractInteraction<InteractionState>} {
  //@ts-ignore
  let instance = new definition.interaction(parentMode, controller, definition.options);

  let proxy: DrawCustomMode & DrawCustomModeThis = {
    onSetup(initialState: InteractionState): any {
      controller.interaction.mode.updateCurrentInteraction(identifier, this, new InteractionState());
      let state: InteractionState = {...instance.buildInitialState(initialState)};

      if (initialState.features?.size) {
        state.features = new Map();
        initialState.features.forEach(feature => {
          state.features.set((feature.id || feature.properties?.id).toString(), JSON.parse(JSON.stringify(feature)));
        });

        state.featureID = initialState.featureID;
        state.isDirty = initialState.isDirty;
      }
      controller.mapInstance.fire('draw.mode_init', {identifier, mode: this, state});

      return state;
    },
    toDisplayFeatures: (state: any, geojson: GeoJSON, display: (geojson: GeoJSON) => void,) => instance.display(state, geojson, display),
    onStop: (state: any) => instance.onStop(state),
    onClick: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => {
      await instance.onClick(state, e);
      if (e.defaultPrevented) {
        controller.interaction.lockClick();
      }
    },
    onMouseMove: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onMouseMove(state, e),
    onMouseDown: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onMouseDown(state, e),
    onMouseUp: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onMouseUp(state, e),
    onMouseOut: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onMouseOut(state, e),
    onKeyUp: async (state: InteractionState, e: KeyboardEvent) => await instance.onKeyUp(state, e),
    onKeyDown: async (state: InteractionState, e: KeyboardEvent) => await instance.onKeyDown(state, e),
    onDrag: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onDrag(state, e),
    onTouchStart: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onTouchStart(state, e),
    onTouchEnd: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onTouchEnd(state, e),
    onTouchMove: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => await instance.onTouchMove(state, e),
    onTap: async (state: InteractionState, e: mapboxgl.MapMouseEvent) => {
      await instance.onClick(state, e)
      if (e.lngLat)
        controller.mapInstance.flyTo({center: [e.lngLat.lng, e.lngLat.lat]})
      if (e.defaultPrevented)
        controller.interaction.lockClick();
    },
  } as DrawCustomMode | any;

  return {proxy, instance}
}

export interface DrawIconOptions {
  'icon-size': number;
  'icon-flat': boolean;
  'icon-anchor': string;
}
